import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve persistent visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('ab_visitor_id');
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('ab_visitor_id', visitorId);
  }
  return visitorId;
};

// A/B Testing Context
const ABTestingContext = createContext(null);

// Provider Component
export const ABTestingProvider = ({ children }) => {
  const [experiments, setExperiments] = useState({});
  const [variants, setVariants] = useState({});
  const [loading, setLoading] = useState(false);
  const visitorId = getVisitorId();

  // Get variant for a specific experiment
  const getVariant = useCallback(async (experimentId) => {
    // Check cache first
    if (variants[experimentId]) {
      return variants[experimentId];
    }

    try {
      const response = await axios.get(
        `${API}/api/ab-testing/experiments/${experimentId}/variant`,
        { params: { visitor_id: visitorId } }
      );

      if (response.data.status === 'success' && response.data.variant) {
        const variant = response.data.variant;
        setVariants(prev => ({ ...prev, [experimentId]: variant }));
        return variant;
      }
    } catch (error) {
      console.error('Error fetching variant:', error);
    }
    return null;
  }, [visitorId, variants]);

  // Get active experiments for a page
  const getActiveExperiments = useCallback(async (page) => {
    if (experiments[page]) {
      return experiments[page];
    }

    try {
      const response = await axios.get(`${API}/api/ab-testing/active`, {
        params: { page }
      });

      if (response.data.status === 'success') {
        const pageExperiments = response.data.experiments || [];
        setExperiments(prev => ({ ...prev, [page]: pageExperiments }));
        return pageExperiments;
      }
    } catch (error) {
      console.error('Error fetching experiments:', error);
    }
    return [];
  }, [experiments]);

  // Track conversion event
  const trackConversion = useCallback(async (experimentId, variantId, eventType, eventData = {}) => {
    try {
      await axios.post(`${API}/api/ab-testing/track`, {
        experiment_id: experimentId,
        variant_id: variantId,
        visitor_id: visitorId,
        event_type: eventType,
        event_data: eventData
      });
      console.log(`[A/B] Tracked: ${eventType} for variant ${variantId}`);
      return true;
    } catch (error) {
      console.error('Error tracking conversion:', error);
      return false;
    }
  }, [visitorId]);

  // Shortcut: Track with current variant
  const trackEvent = useCallback(async (experimentId, eventType, eventData = {}) => {
    const variant = variants[experimentId];
    if (!variant) {
      console.warn(`[A/B] No variant found for experiment ${experimentId}`);
      return false;
    }
    return trackConversion(experimentId, variant.id, eventType, eventData);
  }, [variants, trackConversion]);

  const value = {
    visitorId,
    variants,
    experiments,
    loading,
    getVariant,
    getActiveExperiments,
    trackConversion,
    trackEvent
  };

  return (
    <ABTestingContext.Provider value={value}>
      {children}
    </ABTestingContext.Provider>
  );
};

// Hook to use A/B testing
export const useABTesting = () => {
  const context = useContext(ABTestingContext);
  if (!context) {
    throw new Error('useABTesting must be used within ABTestingProvider');
  }
  return context;
};

// Hook for page-specific A/B testing
export const usePageABTest = (page) => {
  const { getActiveExperiments, getVariant, trackEvent, visitorId } = useABTesting();
  const [variant, setVariant] = useState(null);
  const [experimentId, setExperimentId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeTest = async () => {
      setLoading(true);
      try {
        const activeExperiments = await getActiveExperiments(page);
        
        if (activeExperiments.length > 0) {
          const experiment = activeExperiments[0];
          setExperimentId(experiment.id);
          
          const assignedVariant = await getVariant(experiment.id);
          setVariant(assignedVariant);
        }
      } catch (error) {
        console.error('Error initializing A/B test:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeTest();
  }, [page, getActiveExperiments, getVariant]);

  const track = useCallback((eventType, eventData = {}) => {
    if (experimentId) {
      return trackEvent(experimentId, eventType, eventData);
    }
    return Promise.resolve(false);
  }, [experimentId, trackEvent]);

  return {
    variant,
    experimentId,
    loading,
    track,
    visitorId,
    isControlVariant: variant?.is_control === true,
    variantName: variant?.name || 'control',
    variantConfig: variant?.config || {}
  };
};

export default ABTestingProvider;
